import {
  IconBoldLine,
  IconCodeLine,
  IconItalicLine,
  IconStrikethroughLine,
  IconUnderlineLine,
} from "@instructure/ui";
import { $isCodeNode, getDefaultCodeLanguage } from "@lexical/code";
import { $isLinkNode } from "@lexical/link";
import { $isListNode, ListNode } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isHeadingNode } from "@lexical/rich-text";
import { $isAtNodeEnd } from "@lexical/selection";
import { $getNearestNodeOfType, IS_APPLE, mergeRegister } from "@lexical/utils";
import { useTranslation } from "react-i18next";
import {
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  KEY_DOWN_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from "lexical";
import { useCallback, useEffect, useRef, useState } from "react";
import { CodeLanguagePlugin } from "../CodeLanguagePlugin";
import { ImagePlugin } from "../ImagePlugin";
import { LinkPlugin } from "../LinkPlugin";
import { SHORTCUTS } from "../ShortcutsPlugin";
import { TablePlugin } from "../TablePlugin";
import { TextPlugin } from "../TextPlugin";

const LowPriority = 1;

const supportedBlockTypes = new Set([
  "paragraph",
  "quote",
  "code",
  "h1",
  "h2",
  "ul",
  "ol",
]);

function Divider() {
  return <div className="divider" />;
}

function getSelectedNode(selection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

export default function ToolbarPlugin() {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState("paragraph");
  const [selectedElementKey, setSelectedElementKey] = useState(null);
  const [codeLanguage, setCodeLanguage] = useState("");
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        setSelectedElementKey(elementKey);
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          setBlockType(type);
          if ($isCodeNode(element)) {
            setCodeLanguage(element.getLanguage() || getDefaultCodeLanguage());
          }
        }
      }
      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsCode(selection.hasFormat("code"));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  // Custom keyboard shortcuts
  useEffect(() => {
    editor.registerCommand(
      KEY_DOWN_COMMAND,
      (e) => {
        const modKey = IS_APPLE ? e.metaKey : e.ctrlKey;
        // Code Block
        if (modKey && e.shiftKey && e.key === "c") {
          e.preventDefault();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
          return true;
        }
        // Strikethrough
        if (modKey && e.shiftKey && e.key === "x") {
          e.preventDefault();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
          return true;
        }
        // Left Align
        if (modKey && e.shiftKey && e.key === "l") {
          e.preventDefault();
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
          return true;
        }
        // Center Align
        if (modKey && e.shiftKey && e.key === "e") {
          e.preventDefault();
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
          return true;
        }
        // Right Align
        if (modKey && e.shiftKey && e.key === "r") {
          e.preventDefault();
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
          return true;
        }
        // Justify Align
        if (modKey && e.shiftKey && e.key === "j") {
          e.preventDefault();
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify");
          return true;
        }
        return false;
      },
      2
    );
  }, [editor]);

  return (
    <div className="toolbar" ref={toolbarRef}>
      <button
        disabled={!canUndo}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND);
        }}
        className="toolbar-item spaced"
        aria-label={t("rte.undo")}
        title={`${t("rte.undo")} (${SHORTCUTS.UNDO})`}
      >
        <i className="format undo" />
      </button>
      <button
        disabled={!canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND);
        }}
        className="toolbar-item"
        aria-label={t("rte.redo")}
        title={`${t("rte.redo")}  (${SHORTCUTS.REDO})`}
      >
        <i className="format redo" />
      </button>
      <Divider />
      {supportedBlockTypes.has(blockType) && (
        <>
          <TextPlugin editor={editor} blockType={blockType} />
          <Divider />
        </>
      )}
      {blockType === "code" ? (
        <CodeLanguagePlugin
          editor={editor}
          codeLanguage={codeLanguage}
          selectedElementKey={selectedElementKey}
        />
      ) : (
        <>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
            }}
            className={"toolbar-item spaced " + (isBold ? "active" : "")}
            aria-label={`${t("rte.format")} ${t("rte.bold")}`}
            title={`${t("rte.bold")} (${SHORTCUTS.BOLD})`}
          >
            <IconBoldLine size="x-small" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
            }}
            className={"toolbar-item spaced " + (isItalic ? "active" : "")}
            aria-label={`${t("rte.format")} ${t("rte.italic")}`}
            title={`${t("rte.italic")} (${SHORTCUTS.ITALIC})`}
          >
            <IconItalicLine size="x-small" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
            }}
            className={"toolbar-item spaced " + (isUnderline ? "active" : "")}
            aria-label={`${t("rte.format")} ${t("rte.underline")}`}
            title={`${t("rte.underline")} (${SHORTCUTS.UNDERLINE})`}
          >
            <IconUnderlineLine size="x-small" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
            }}
            className={
              "toolbar-item spaced " + (isStrikethrough ? "active" : "")
            }
            aria-label={`${t("rte.format")} ${t("rte.strikethrough")}`}
            title={`${t("rte.strikethrough")} (${SHORTCUTS.STRIKETHROUGH})`}
          >
            <IconStrikethroughLine size="x-small" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
            }}
            className={"toolbar-item spaced " + (isCode ? "active" : "")}
            aria-label={t("rte.insertCodeBlock")}
            title={`${t("rte.insertCodeBlock")} (${SHORTCUTS.CODE_BLOCK})`}
          >
            <IconCodeLine size="x-small" />
          </button>
          <LinkPlugin editor={editor} isLink={isLink} />
          <Divider />
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
            }}
            className="toolbar-item spaced"
            aria-label={t("rte.leftAlign")}
            title={`${t("rte.leftAlign")} (${SHORTCUTS.LEFT_ALIGN})`}
          >
            <i className="format left-align" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
            }}
            className="toolbar-item spaced"
            aria-label={t("rte.centerAlign")}
            title={`${t("rte.centerAlign")} (${SHORTCUTS.CENTER_ALIGN})`}
          >
            <i className="format center-align" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
            }}
            className="toolbar-item spaced"
            aria-label={t("rte.rightAlign")}
            title={`${t("rte.rightAlign")} (${SHORTCUTS.RIGHT_ALIGN})`}
          >
            <i className="format right-align" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify");
            }}
            className="toolbar-item spaced"
            aria-label={t("rte.justifyAlign")}
            title={`${t("rte.justifyAlign")} (${SHORTCUTS.JUSTIFY_ALIGN})`}
          >
            <i className="format justify-align" />
          </button>
          <TablePlugin />
          <ImagePlugin />
        </>
      )}
    </div>
  );
}
